import type {
  AuditReceipt,
  OperatingSnapshot,
  ResponseOption,
  ScenarioStage,
} from "../../domain";
import { selectOperatingSnapshot } from "../../simulation/guidedOperationsSelectors.ts";

export type TelecomTone = "healthy" | "warning" | "critical" | "neutral";

export type TelecomActivity = {
  id: string;
  time: string;
  title: string;
  detail: string;
  tone: TelecomTone;
  recordId?: string;
};

export type TelecomStory = {
  title: string;
  summary: string;
  nextAction: string;
  actionTarget: "incident" | "trace";
};

export function telecomSnapshot(
  stage: ScenarioStage,
  decisionId: ResponseOption["id"] | null,
): OperatingSnapshot {
  return selectOperatingSnapshot({ stage, decisionId });
}

export function telecomStory(
  stage: ScenarioStage,
  linkedEvidenceCount: number,
  decisionId: ResponseOption["id"] | null,
): TelecomStory {
  if (stage === "calm") {
    return {
      title: "Campus operations are stable",
      summary: "Review the deterministic baseline, then start the controlled North Academic outage from the primary action above.",
      nextAction: "Inspect the campus baseline",
      actionTarget: "trace",
    };
  }

  if (stage === "detected" || stage === "investigating") {
    return {
      title: "Diagnose North Academic impact",
      summary: `${linkedEvidenceCount} evidence record${linkedEvidenceCount === 1 ? " is" : "s are"} linked. Connect the change, switch, service, and endpoint evidence before choosing a response.`,
      nextAction: "Continue evidence review",
      actionTarget: "incident",
    };
  }

  if (stage === "identified") {
    return {
      title: "Integrate evidence and ownership",
      summary: "The likely trunk fault is identified. Review readiness, ownership, and response consequences before a human decision.",
      nextAction: "Review response readiness",
      actionTarget: "incident",
    };
  }

  if (stage === "mitigating") {
    return {
      title: "Validate the approved response",
      summary: "The response is being applied under the recorded human decision and readiness evidence.",
      nextAction: "Inspect response evidence",
      actionTarget: "incident",
    };
  }

  if (stage === "monitoring") {
    return {
      title: "Verify recovery",
      summary: `${decisionId ? "The selected response is active." : "Response work is active."} Confirm service recovery and TEL-0318 re-registration before closure.`,
      nextAction: "Verify service and phone recovery",
      actionTarget: "incident",
    };
  }

  return {
    title: "Recovery is verified",
    summary: "North Academic service and endpoint checks passed. Review receipts, follow-up work, and closure artifacts.",
    nextAction: "Review accountable closure",
    actionTarget: "incident",
  };
}

function toneForReceipt(receipt: AuditReceipt, stage: ScenarioStage): TelecomTone {
  if (receipt.kind === "verification") return "healthy";
  if (receipt.kind === "decision" && stage === "recovered") return "healthy";
  if (receipt.kind === "scenario") return "critical";
  if (
    receipt.kind === "evidence"
    || receipt.kind === "override"
    || receipt.kind === "decision"
  ) {
    return "warning";
  }
  return "neutral";
}

export function telecomActivity(
  stage: ScenarioStage,
  receipts: AuditReceipt[],
): TelecomActivity[] {
  if (receipts.length === 0) {
    const snapshot = telecomSnapshot(stage, null);
    return [
      {
        id: stage === "calm" ? "baseline-campus" : `stage-${stage}`,
        time: String(snapshot.switchHealth.lastCheck.value),
        title: stage === "calm"
          ? "Deterministic campus baseline confirmed"
          : `Scenario state: ${stage}`,
        detail: stage === "calm"
          ? `${snapshot.wifi.coverage.value} ${snapshot.wifi.coverage.unit} buildings have full coverage; ${snapshot.voice.registered.value} ${snapshot.voice.registered.unit} desk phones are registered.`
          : "Open the incident workspace to continue the accountable response.",
        tone: stage === "calm" ? "healthy" : "warning",
        recordId: stage === "calm" ? undefined : "INC-2048",
      },
    ];
  }

  return receipts.slice(0, 6).map((receipt) => ({
    id: receipt.id,
    time: receipt.occurredAt,
    title: `${receipt.actor}: ${receipt.action}`,
    detail: receipt.outcome,
    tone: toneForReceipt(receipt, stage),
    recordId: receipt.recordId,
  }));
}
